import { DEFAULT_WEIGHTS } from "../../utils/scoreCalculator";
import { formatScore } from "../../utils/format";
import RatingPill from "./RatingPill";

export default function ScoreBreakdown({
  performance = {}, weights = DEFAULT_WEIGHTS, title = "Score breakdown", provisional = false, restricted = false, scoreCap = null,
}) {
  const hideNumbers = restricted && provisional;
  const showWeights = !restricted;
  const hierarchical = performance.scoringMode === "HIERARCHICAL_KRA_KPI";
  const supplied = performance.effectiveWeights || {};
  const effective = {
    goals: Number(supplied.goals ?? supplied.GOAL ?? weights.goals),
    kra: Number(supplied.kra ?? supplied.KRA ?? (hierarchical ? Number(weights.kra) + Number(weights.kpi) : weights.kra)),
    kpi: Number(supplied.kpi ?? supplied.KPI ?? (hierarchical ? 0 : weights.kpi)),
  };
  const parts = hierarchical
    ? [
      { key: "goalScore", label: "Goals", weight: effective.goals },
      { key: "kraScore", label: "KRA (derived from KPIs)", weight: effective.kra },
    ]
    : [
      { key: "goalScore", label: "Goals", weight: effective.goals },
      { key: "kraScore", label: "KRA", weight: effective.kra },
      // [REMOVED] Requirement 3: the separate KPI score is no longer shown (KPIs only feed their KRA).
    ];
  const final = performance.finalScore;
  const barMax = scoreCap && scoreCap > 100 ? scoreCap : 100;
  return (
    <div className="score-breakdown">
      <div className="score-final">
        <p className="score-final-label">{title}</p>
        {hideNumbers ? (
          <>
            <p className="score-final-value">Pending</p>
            <p className="score-note">
              The official score and rating are calculated by the system with the cycle&apos;s scoring rules
              and appear after HR finalizes. The bars below only show how complete each part is.
            </p>
          </>
        ) : (
          <>
            <p className="score-final-value">
              {formatScore(final)}
              {scoreCap ? <span>/{formatScore(scoreCap)}</span> : null}
            </p>
            <RatingPill rating={performance.rating} tone={performance.ratingTone} />
            {provisional && final !== null && final !== undefined && <p className="score-note">Provisional until HR finalizes.</p>}
          </>
        )}
      </div>
      <div className="score-parts">
        {parts.map((p) => {
          const score = performance[p.key];
          const contribution = score === null || score === undefined ? null : (score * p.weight) / 100;
          return (
            <div key={p.key} className="score-part">
              <div className="score-part-head">
                <span className="score-part-label">{p.label}</span>
                {showWeights && <span className="score-part-weight">{p.weight}% weight</span>}
              </div>
              <div className="score-part-bar">
                <span className="score-part-fill" style={{ width: `${Math.max(0, Math.min(100, ((score ?? 0) / barMax) * 100))}%` }} />
              </div>
              <div className="score-part-foot">
                <span>{hideNumbers ? "Estimate" : "Score"} {formatScore(score)}</span>
                {showWeights && <span>Adds {formatScore(contribution, 2)} points</span>}
              </div>
            </div>
          );
        })}
        {showWeights && (
          <p className="score-formula">
            {hierarchical
              ? `Final score = Goals x ${effective.goals}% + KRA/KPI hierarchy x ${effective.kra}%`
              : `Final score = Goals x ${effective.goals}% + KRA x ${effective.kra}%`}
          </p>
        )}
      </div>
    </div>
  );
}
